import { useMemo, useState, useEffect } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { GET_APPROVER_INVOICES } from '../../services/graphql';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import keycloak from '../../keycloak';
import { getTableDensityStyles, normalizeTableDensity } from '../../config/tableDensity';

const originalColumns = [
    {
        accessorKey: 'date',
        header: 'Date',
        type: "date",
        size: 80,
        dateSetting: { locale: "en-GB" },
    },
    {
        accessorKey: 'invoiceID',
        header: '#',
        size: 50,
    },
    {
        accessorKey: 'supplierEmail',
        header: 'Submitted By',
        size: 180,
    },
    {
        accessorKey: 'expenseType',
        header: 'Expense Type',
        size: 150,
    },
    {
        accessorKey: 'category',
        header: 'Category',
        size: 150,
    },
    {
        accessorKey: 'subCategory',
        header: 'Sub-category',
        size: 150,
    },
    {
        accessorKey: 'originalAmount',
        header: 'Sub Total ($)',
        size: 150,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
    },
    {
        accessorKey: 'paidDate',
        header: 'Remittance Date',
        type: "date",
        size: 100,
        dateSetting: { locale: "en-GB" },
    },
    {
        accessorKey: 'GST',
        header: 'GST ($)',
        size: 80,
    },
    {
        accessorKey: 'PST',
        header: 'PST ($)',
        size: 80,
    },
    {
        accessorKey: 'distance',
        header: 'Distance',
        size: 100,
    },
    {
        accessorKey: 'project',
        header: 'Project',
        size: 100,
    },
    {
        accessorKey: 'entity',
        header: 'Entity',
        size: 100,
    },
    {
        accessorKey: 'allocation',
        header: 'Allocation',
        size: 150,
    },
];

const ApproverDashboardTable = ({
    selectedColumns,
    rowSelection,
    setRowSelection,
    activeTab,
    data,
    setData,
    showColumnFilters,
    globalFilter,
    onGlobalFilterChange,
}) => {
    const navigate = useNavigate();
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnSorts, setColumnSorts] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5,
    });
    const [rowCount, setRowCount] = useState();  
    const tableDensity = normalizeTableDensity(localStorage.getItem('tableDensity'));
    const densityStyles = getTableDensityStyles(tableDensity);
    const isDarkMode = useSelector(state => state.graphql.darkMode);

    const customIcons = {
        SearchIcon: () => <SearchIcon color='primary' />,
        SearchOffIcon: () => <SearchIcon color='primary' />,
        FilterListIcon: () => <TuneIcon color='primary' />,
    };

    const columns = useMemo(() => {
        return originalColumns.filter((column) =>
            selectedColumns.includes(column.accessorKey)
        );
    }, [selectedColumns, originalColumns]);

    function convertAndStringifyFilters(inputArray) {
        let andFilter = [];

        if (columnFilters.length > 0) {
            const convertedArray = inputArray.map(inputObject => {
                return {
                    field: inputObject.id,
                    operation: "contains",
                    value: inputObject.value
                };
            });
            andFilter = andFilter.concat(convertedArray);
        }
        
        if (activeTab === 'pending') {
            andFilter.push({
                field: 'status',
                operation: 'in',
                value: ['Pending', 'Processing']
            });
        } else if (activeTab === 'approved') {
        andFilter.push({
            field: 'status',
            operation: 'contains',
            value: 'Approved'
        });
        } else if (activeTab === 'rejected') {
        andFilter.push({
            field: 'status',
            operation: 'contains',
            value: 'Rejected'
        });
        } else if (activeTab === 'editRequired') {
        andFilter.push({
            field: 'status',
            operation: 'contains',
            value: 'EditRequired'
        });
        }

        let email = keycloak.tokenParsed?.email; ;
        if (activeTab === 'pending') {
            andFilter.push({
                field: 'approverEmails',
                operation: 'endsWith',
                value: email
            });
        } else {
            andFilter.push({
                field: 'approverEmails',
                operation: 'contains',
                value: email
            });
        }
        

        return JSON.stringify(andFilter);
    }

    function convertAndStringifyGlobalFilter(globalFilter) {
        // or filter for all selected columns
        const convertedArray = columns.map(column => {
            return {
                field: column.id,
                operation: "contains",
                value: globalFilter,
            };
        });
        return JSON.stringify(convertedArray);
    }

    const { loading, error, data1, refetch } = useQuery(GET_APPROVER_INVOICES);
    if (error) {
        alert('Unable to load invoices.');
    }

    useEffect(() => {
        let variables = {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            sortBy: columnSorts.length > 0 ? columnSorts[0].id : null,
            sortOrder: columnSorts.length > 0 ? columnSorts[0].desc : null,
            filter: {
                andFilter: null,
                orFilter: null,
            }
        };

        // to obtain the total number of invoices with the current filter
        let variablesTotal = {
            page: 1,
            pageSize: Infinity,
            filter: {
                andFilter: null,
                orFilter: null,
            }
        };

        const andFilter = convertAndStringifyFilters(columnFilters);
        const orFilter = globalFilter && convertAndStringifyGlobalFilter(globalFilter);

        if (andFilter) { 
            variables.filter.andFilter = andFilter; 
            variablesTotal.filter.andFilter = andFilter;
        }
        else { 
            variables.filter.andFilter = null; 
            variablesTotal.filter.andFilter = null;
        }

        if (orFilter) { 
            variables.filter.orFilter = orFilter; 
            variablesTotal.filter.orFilter = orFilter; 
        }
        else { 
            variables.filter.orFilter = null; 
            variablesTotal.filter.orFilter = null; 
        }

        // to get the total # of invoices with the set filtering, and set row count for correct front-end pagination
        refetch(variablesTotal)
            .then(({ data }) => {
                setRowCount(data.getApproverInvoices.body.length);
            })
            .catch(error => {
                console.error('Error during refetch:', error);
            });

        refetch(variables)
            .then(({ data }) => {
                const invoices = data.getApproverInvoices.body;
                // Debug pending tab load: shows every invoice returned for the current approver.
                if (activeTab === 'pending') {
                    console.log('Pending approver invoices:', invoices);
                }
                setData(invoices);
            })
            .catch(error => {
                console.error('Error during refetch:', error);
            });
    }, [pagination, columnFilters, columnSorts, globalFilter, activeTab]);

    const table = useMaterialReactTable({
        columns,
        data, 
        initialState: { density: densityStyles.density, showColumnFilters: false }, //spacious, comfortable, and compact , assign depending on what was selected by user in settings
        muiTableHeadRowProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiTableHeadCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.headCell
                : {},
        }),
        muiTableBodyCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.bodyCell
                : {},
        }),
        enableColumnResizing: true,
        muiPaginationProps: {
            color: 'primary',
            shape: 'rounded',
            showRowsPerPage: true,
            variant: 'outlined',
        },
        paginationDisplayMode: 'pages',
        mrtTheme: {
            baseBackgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        },
        muiTablePaperProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiBottomToolbarProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)', color: 'var(--theme-text)' },
        },
        enableTopToolbar: false,
        enableHiding: false,
        icons: customIcons,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        enableRowSelection:true,
        onRowSelectionChange: setRowSelection,
        manualFiltering: true,  // server-side filtering
        manualPagination: true, //server-side pagination
        manualSorting: true, //server-side sorting
        rowCount,
        onGlobalFilterChange: onGlobalFilterChange,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setColumnSorts,
        state: {
            columnFilters,
            pagination,
            sorting: columnSorts,
            rowSelection,
            globalFilter,
            showColumnFilters,
            showSkeletons: rowCount === undefined,
        },
        muiTableBodyRowProps: ({ row }) => ({
            onDoubleClick: () => {
                const id = row.original['invoiceID'];
                const category = row.original['category'];
                const status = row.original['status'];
                sessionStorage.setItem("selectedInvoiceID", id);
                sessionStorage.setItem("selectedInvoiceStatus", status || "");
                sessionStorage.setItem("approverActiveTab", activeTab);
                navigate(`/approve-invoices/details/${category}/${id}`);
            },
            sx: {
                cursor: 'pointer',
                backgroundColor: 'var(--theme-bg-elevated)',
                ...densityStyles.bodyRow,
            },
            onTouchEnd: () => {
                const id = row.original['invoiceID'];
                const category = row.original['category'];
                const status = row.original['status'];
                sessionStorage.setItem("selectedInvoiceID", id);
                sessionStorage.setItem("selectedInvoiceStatus", status || "");
                sessionStorage.setItem("approverActiveTab", activeTab);
                navigate(`/approve-invoices/details/${category}/${id}`);
            },
        }),
    });

    return <MaterialReactTable table={table} />;
};

export default ApproverDashboardTable;
